import type { CommerceCalculationRuntimeItem, LocalProjectionPayload } from "../gateway/local-projection-client";
import { bindLocalCartCommerceCalculation, readLocalCart } from "./cart-state";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

function scopeKey(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:commerce-calculation`;
}

function recordKey(identity: TrustedTerminalIdentity) {
  return `runtime:commerce-calculation:${scopeKey(identity)}`;
}

function normalizeCommerceCalculation(value: unknown): CommerceCalculationRuntimeItem | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Partial<CommerceCalculationRuntimeItem>;
  if (
    typeof item.settings_version_id !== "string" || !item.settings_version_id ||
    typeof item.version_no !== "number" || !Number.isInteger(item.version_no) || item.version_no <= 0 ||
    item.rounding_scope !== "ALL_PAYMENTS" ||
    !["NONE", "NEAREST", "DOWN", "UP"].includes(item.rounding_mode ?? "") ||
    typeof item.rounding_increment !== "number" || !Number.isFinite(item.rounding_increment) || item.rounding_increment <= 0
  ) return null;
  return item as CommerceCalculationRuntimeItem;
}

export async function applyCommerceCalculationRuntime(
  identity: TrustedTerminalIdentity,
  payload: LocalProjectionPayload<"COMMERCE_CALCULATION">,
) {
  const item = normalizeCommerceCalculation(payload.items[0] ?? null);
  const key = recordKey(identity);
  if (!item) {
    await posRuntimeDb.runtimeMeta.delete(key);
    return;
  }
  await posRuntimeDb.runtimeMeta.put({
    key,
    scopeKey: scopeKey(identity),
    value: item,
    updatedAt: payload.server_at,
  });
}

/**
 * Read the newest Published + Effective calculation settings already present
 * in the local projection. This intentionally ignores an active-cart binding
 * and is used only at explicit stale/revalidation boundaries such as checkout.
 */
export async function readPublishedCommerceCalculationRuntime(
  identity: TrustedTerminalIdentity,
): Promise<CommerceCalculationRuntimeItem | null> {
  const row = await posRuntimeDb.runtimeMeta.get(recordKey(identity));
  return normalizeCommerceCalculation(row?.value ?? null);
}

/**
 * Resolve the effective calculation settings for the current POS interaction.
 * Published runtime remains fresh in runtimeMeta, but an active cart keeps the
 * settings snapshot captured when that cart started. Once the cart is cleared,
 * the next interaction automatically receives the latest published version.
 */
export async function readCommerceCalculationRuntime(
  identity: TrustedTerminalIdentity,
): Promise<CommerceCalculationRuntimeItem | null> {
  const cart = await readLocalCart(identity);
  if (cart.lines.length && cart.commerceCalculation) return cart.commerceCalculation;

  const latest = await readPublishedCommerceCalculationRuntime(identity);
  if (!latest) return null;

  if (cart.lines.length) {
    const bound = await bindLocalCartCommerceCalculation(identity, cart, latest);
    return bound.commerceCalculation ?? latest;
  }

  return latest;
}
